import { useEffect, useState } from 'react';
import {
  Search,
  Globe,
  Mail,
  Phone,
  Star,
  ArrowRight,
  RefreshCw,
  Check,
  X,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Tooltip from '@/components/ui/Tooltip';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

interface Prospect {
  id: string;
  name: string;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_name: string | null;
  contact_form_url: string | null;
  integration_type: string;
  integration_notes: string | null;
  payment_methods: string[] | null;
  shipping_methods: string[] | null;
  min_order_amount: number | null;
  min_order_qty: number | null;
  lead_time_days: number | null;
  return_policy_summary: string | null;
  product_categories: string[] | null;
  product_focus: string | null;
  dropship_program: boolean;
  research_notes: string | null;
  research_sources: Array<{ url: string; title?: string }>;
  confidence_score: number;
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ProspectStats {
  total: number;
  discovered: number;
  researching: number;
  researched: number;
  approved: number;
  rejected: number;
  promoted: number;
}

const STATUS_TABS: Array<{ key: string; label: string; statKey?: keyof ProspectStats }> = [
  { key: '', label: 'All', statKey: 'total' },
  { key: 'researched', label: 'Researched', statKey: 'researched' },
  { key: 'discovered', label: 'Discovered', statKey: 'discovered' },
  { key: 'researching', label: 'Researching', statKey: 'researching' },
  { key: 'approved', label: 'Approved', statKey: 'approved' },
  { key: 'promoted', label: 'Promoted', statKey: 'promoted' },
  { key: 'rejected', label: 'Rejected', statKey: 'rejected' },
];

const statusBadge: Record<string, { variant: 'yellow' | 'blue' | 'green' | 'gray' | 'red'; label: string }> = {
  discovered: { variant: 'yellow', label: 'Discovered' },
  researching: { variant: 'blue', label: 'Researching' },
  researched: { variant: 'green', label: 'Researched' },
  approved: { variant: 'green', label: 'Approved' },
  promoted: { variant: 'blue', label: 'Promoted' },
  rejected: { variant: 'red', label: 'Rejected' },
};

function ConfidenceBadge({ score }: { score: number }) {
  const color = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-yellow-600' : 'text-gray-400';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-sm font-medium', color)}>
      <Star className="w-3.5 h-3.5" />
      {score}
    </span>
  );
}

export default function SupplierProspects() {
  const addToast = useUIStore((s) => s.addToast);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [stats, setStats] = useState<ProspectStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [promoteResult, setPromoteResult] = useState<{ supplier_id: string; name: string } | null>(null);

  const fetchProspects = async (p = 1) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('page', p.toString());
      params.set('limit', '25');
      if (statusFilter) params.set('status', statusFilter);
      if (searchQuery) params.set('query', searchQuery);
      const res = await api.get<{ prospects: Prospect[]; total: number }>(`/admin/agent/prospects?${params}`);
      setProspects(res.prospects);
      setTotal(res.total);
      setPage(p);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load prospects', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get<ProspectStats>('/admin/agent/prospects/stats');
      setStats(res);
    } catch {
      // non-critical
    }
  };

  useEffect(() => { fetchProspects(); fetchStats(); }, [statusFilter]);

  const handleAction = async (id: string, action: 'approve' | 'reject' | 'promote' | 'research') => {
    setProcessingId(id);
    try {
      if (action === 'approve' || action === 'reject') {
        await api.patch(`/admin/agent/prospects/${id}`, { status: action === 'approve' ? 'approved' : 'rejected' });
        addToast(`Prospect ${action}d`, 'success');
      } else if (action === 'promote') {
        const res = await api.post<{ supplier_id?: string }>(`/admin/agent/prospects/${id}/promote`);
        addToast('Promoted to supplier', 'success');
        const promoted = prospects.find((p) => p.id === id);
        if (res.supplier_id && promoted) {
          setPromoteResult({ supplier_id: res.supplier_id, name: promoted.name });
        }
      } else if (action === 'research') {
        await api.post(`/admin/agent/prospects/${id}/research`);
        addToast('Research task started', 'success');
      }
      fetchProspects(page);
      fetchStats();
      if (showDetail) setShowDetail(false);
    } catch (err) {
      addToast(err instanceof Error ? err.message : `Failed to ${action}`, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const openDetail = async (id: string) => {
    try {
      const res = await api.get<Prospect>(`/admin/agent/prospects/${id}`);
      setSelectedProspect(res);
      setShowDetail(true);
    } catch (err) {
      addToast('Failed to load prospect details', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Supplier Prospects</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-900' },
            { label: 'Discovered', value: stats.discovered, color: 'text-yellow-600' },
            { label: 'Researching', value: stats.researching, color: 'text-blue-600' },
            { label: 'Researched', value: stats.researched, color: 'text-green-600' },
            { label: 'Approved', value: stats.approved, color: 'text-green-700' },
            { label: 'Promoted', value: stats.promoted, color: 'text-blue-700' },
            { label: 'Rejected', value: stats.rejected, color: 'text-red-600' },
          ].map((s) => (
            <Card key={s.label} className="p-3 text-center">
              <div className={cn('text-xl font-bold', s.color)}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1 overflow-x-auto">
          {STATUS_TABS.map((tab) => {
            const count = stats && tab.statKey ? stats[tab.statKey] : undefined;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors',
                  statusFilter === tab.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {tab.label}
                {count != null && <span className="ml-1.5 opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search name or website..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchProspects()}
            className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-md"
          />
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Focus</th>
                <th className="px-4 py-3">
                  <span className="inline-flex items-center gap-1">
                    Confidence
                    <Tooltip content="≥70 high (green) · 40-69 medium (yellow) · <40 low (gray)" />
                  </span>
                </th>
                <th className="px-4 py-3">Integration</th>
                <th className="px-4 py-3">Dropship</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Loading...
                  </td>
                </tr>
              ) : prospects.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No prospects found
                  </td>
                </tr>
              ) : (
                prospects.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openDetail(p.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      {p.website && (
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {p.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">
                      {p.product_focus || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <ConfidenceBadge score={p.confidence_score} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {p.integration_type === 'unknown' ? '—' : p.integration_type}
                    </td>
                    <td className="px-4 py-3">
                      {p.dropship_program ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusBadge[p.status]?.variant || 'gray'}>
                        {statusBadge[p.status]?.label || p.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {(p.status === 'researched' || p.status === 'approved') && (
                          <button
                            onClick={() => handleAction(p.id, 'promote')}
                            disabled={processingId === p.id}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Promote to Supplier"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                        {p.status === 'discovered' && (
                          <button
                            onClick={() => handleAction(p.id, 'research')}
                            disabled={processingId === p.id}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Research"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        {!['promoted', 'rejected'].includes(p.status) && (
                          <button
                            onClick={() => handleAction(p.id, 'reject')}
                            disabled={processingId === p.id}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {total > 25 && (
          <div className="flex justify-between items-center px-4 py-3 border-t text-sm text-gray-500">
            <span>{total} prospects</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchProspects(page - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={page * 25 >= total} onClick={() => fetchProspects(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title={selectedProspect?.name || 'Prospect'}>
        {selectedProspect && <ProspectDetail prospect={selectedProspect} onAction={handleAction} processingId={processingId} />}
      </Modal>

      {/* Promote success modal */}
      <Modal
        isOpen={promoteResult !== null}
        onClose={() => setPromoteResult(null)}
        title="Promoted ✓"
      >
        {promoteResult && (
          <div className="space-y-4" data-testid="promote-success-modal">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{promoteResult.name}</span> is now in your Suppliers list. It's
              inactive — configure ordering and contact details to start using it.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPromoteResult(null)}>
                Stay here
              </Button>
              <Link
                to={`/admin/suppliers/${promoteResult.supplier_id}/edit`}
                onClick={() => setPromoteResult(null)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700"
              >
                View in Suppliers
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function ProspectDetail({
  prospect: p,
  onAction,
  processingId,
}: {
  prospect: Prospect;
  onAction: (id: string, action: 'approve' | 'reject' | 'promote' | 'research') => void;
  processingId: string | null;
}) {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Badge variant={statusBadge[p.status]?.variant || 'gray'} className="mb-2">
            {statusBadge[p.status]?.label || p.status}
          </Badge>
          <div className="flex items-center gap-3 mt-1">
            <ConfidenceBadge score={p.confidence_score} />
            {p.dropship_program && <Badge variant="green">Dropship Program</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {p.status === 'discovered' && (
            <Button size="sm" onClick={() => onAction(p.id, 'research')} disabled={processingId === p.id}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Research
            </Button>
          )}
          {(p.status === 'researched' || p.status === 'approved') && (
            <Button size="sm" onClick={() => onAction(p.id, 'promote')} disabled={processingId === p.id}>
              <ArrowRight className="w-3.5 h-3.5 mr-1" /> Promote
            </Button>
          )}
          {!['promoted', 'rejected'].includes(p.status) && (
            <>
              <Button size="sm" variant="outline" onClick={() => onAction(p.id, 'approve')} disabled={processingId === p.id}>
                <Check className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onAction(p.id, 'reject')} disabled={processingId === p.id}>
                <X className="w-3.5 h-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Contact & Website */}
      <div className="grid grid-cols-2 gap-4">
        <Section title="Contact">
          <InfoRow icon={<Globe className="w-4 h-4" />} label="Website" value={p.website} link />
          <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={p.contact_email} />
          <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={p.contact_phone} />
          <InfoRow label="Contact Name" value={p.contact_name} />
          <InfoRow label="Contact Form" value={p.contact_form_url} link />
        </Section>

        <Section title="Business Terms">
          <InfoRow label="Integration" value={p.integration_type !== 'unknown' ? p.integration_type : null} />
          <InfoRow label="Payment Methods" value={p.payment_methods?.join(', ')} />
          <InfoRow label="Shipping" value={p.shipping_methods?.join(', ')} />
          <InfoRow label="Min Order $" value={p.min_order_amount != null ? `$${p.min_order_amount}` : null} />
          <InfoRow label="Min Order Qty" value={p.min_order_qty?.toString()} />
          <InfoRow label="Lead Time" value={p.lead_time_days != null ? `${p.lead_time_days} days` : null} />
        </Section>
      </div>

      {/* Products */}
      <Section title="Products">
        <InfoRow label="Categories" value={p.product_categories?.join(', ')} />
        <InfoRow label="Focus" value={p.product_focus} />
      </Section>

      {/* Research */}
      {(p.integration_notes || p.return_policy_summary || p.research_notes) && (
        <Section title="Research Notes">
          {p.integration_notes && <NoteBlock label="Integration" text={p.integration_notes} />}
          {p.return_policy_summary && <NoteBlock label="Return Policy" text={p.return_policy_summary} />}
          {p.research_notes && <NoteBlock label="Notes" text={p.research_notes} />}
        </Section>
      )}

      {/* Sources */}
      {p.research_sources && p.research_sources.length > 0 && (
        <Section title="Sources">
          <ul className="space-y-1">
            {p.research_sources.map((s, i) => (
              <li key={i} className="text-sm">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                  {s.title || s.url}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Admin Notes */}
      {p.admin_notes && (
        <Section title="Admin Notes">
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{p.admin_notes}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, value, link }: { icon?: React.ReactNode; label: string; value?: string | null; link?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon && <span className="text-gray-400">{icon}</span>}
      <span className="text-gray-500 min-w-[100px]">{label}:</span>
      {link ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
          {value.replace(/^https?:\/\//, '')}
        </a>
      ) : (
        <span className="text-gray-900 capitalize">{value}</span>
      )}
    </div>
  );
}

function NoteBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="mb-2">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{text}</p>
    </div>
  );
}
